// Bar categories & margin rules: rename / add / delete categories, set the markup used to suggest sell prices.
import React, { useState } from 'react';
import { Tags, Plus, Trash2, Loader2, Check } from 'lucide-react';
import { apiCall } from '../_shared';
import { money } from './BarTab';

export const suggestPrice = (unitCostNet, portions, markup, step = 0.1) => {
  if (!unitCostNet || unitCostNet <= 0 || !markup) return null;
  const raw = (unitCostNet / Math.max(Number(portions) || 1, 0.01)) * markup;
  return Math.round(Math.ceil(raw / step - 1e-9) * step * 100) / 100;
};

const CatRow = ({ c, canManage, onSaved, onDeleted }) => {
  const [label, setLabel] = useState(c.label);
  const [markup, setMarkup] = useState(c.markup);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const dirty = label !== c.label || Number(markup) !== Number(c.markup);
  const save = async () => {
    setBusy(true); setErr('');
    try { onSaved(await apiCall(`/api/admin/bar/categories/${c.id}`, { method: 'PUT', body: JSON.stringify({ label, markup: Number(markup), sort_order: c.sort_order || 0 }) })); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    setBusy(true); setErr('');
    try { await apiCall(`/api/admin/bar/categories/${c.id}`, { method: 'DELETE' }); onDeleted(c.id); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <tr className="border-t" data-testid={`bar-cat-row-${c.id}`}>
      <td className="py-1.5 pl-2"><input value={label} disabled={!canManage} onChange={(e) => setLabel(e.target.value)} className="border rounded px-2 py-0.5 w-full max-w-[200px] text-xs" data-testid={`bar-cat-label-${c.id}`} /></td>
      <td className="py-1.5"><span className="inline-flex items-center gap-1">× <input type="number" step="0.5" min="1" value={markup} disabled={!canManage} onChange={(e) => setMarkup(e.target.value)} className="border rounded px-2 py-0.5 w-16 text-xs" data-testid={`bar-cat-markup-${c.id}`} /></span>
        <span className="ml-2 text-[10px] text-slate-400">e.g. €1.00 net → {money(suggestPrice(1, 1, Number(markup) || 0) || 0)}</span></td>
      <td className="py-1.5 text-right text-slate-500" data-testid={`bar-cat-count-${c.id}`}>{c.products}</td>
      <td className="py-1.5 pr-2 text-right whitespace-nowrap">
        {canManage && dirty && <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-2 py-1 text-[11px] font-semibold mr-1" data-testid={`bar-cat-save-${c.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save</button>}
        {canManage && c.products === 0 && <button type="button" onClick={del} disabled={busy} title="Delete" className="p-1 border rounded hover:bg-red-50" data-testid={`bar-cat-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>}
        {err && <span className="block text-[11px] text-red-600">{err}</span>}
      </td>
    </tr>
  );
};

export const CategoriesEditor = ({ cats, canManage, onChanged }) => {
  const [label, setLabel] = useState('');
  const [markup, setMarkup] = useState(3);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const add = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await apiCall('/api/admin/bar/categories', { method: 'POST', body: JSON.stringify({ label, markup: Number(markup), sort_order: cats.length }) }); setLabel(''); onChanged(); } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const rows = cats.map((c) => <CatRow key={c.id} c={c} canManage={canManage} onSaved={onChanged} onDeleted={onChanged} />);
  return (
    <details className="bg-white border rounded-xl p-3" data-testid="bar-categories">
      <summary className="text-xs font-semibold text-slate-700 cursor-pointer inline-flex items-center gap-1"><Tags className="w-3.5 h-3.5" /> Categories &amp; margin rules ({cats.length})</summary>
      <p className="text-[11px] text-slate-500 mt-2">Markup × net cost per portion = suggested sell price (rounded up to 10 c). Used when products are created from an invoice scan.</p>
      <div className="overflow-x-auto mt-2"><table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="pl-2">Category</th><th>Markup</th><th className="text-right">Products</th><th /></tr></thead><tbody>{rows}</tbody></table></div>
      {canManage && (
        <form onSubmit={add} className="flex flex-wrap items-end gap-2 mt-3 text-xs" data-testid="bar-cat-add-form">
          <label>New category<input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Beer, Cocktails, Breakfast" className="block border rounded px-2 py-1 w-48" data-testid="bar-cat-new-label" /></label>
          <label>Markup<input type="number" step="0.5" min="1" value={markup} onChange={(e) => setMarkup(e.target.value)} className="block border rounded px-2 py-1 w-20" data-testid="bar-cat-new-markup" /></label>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 font-semibold" data-testid="bar-cat-add"><Plus className="w-3 h-3" /> Add</button>
          {err && <span className="text-red-600 w-full" data-testid="bar-cat-err">{err}</span>}
        </form>
      )}
    </details>
  );
};

export default CategoriesEditor;
